import { useEffect } from 'react';

interface AdminDrawerProps {
  open: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  icon?: string;
  iconBg?: string;
  children: React.ReactNode;
  width?: string;
}

export default function AdminDrawer({
  open,
  onClose,
  title,
  subtitle,
  icon,
  iconBg = 'bg-zinc-700',
  children,
  width = 'w-[480px]',
}: AdminDrawerProps) {
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    if (open) window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [open, onClose]);

  return (
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 z-40 bg-black/60 backdrop-blur-sm transition-opacity duration-300 ${open ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}
        onClick={onClose}
      />

      {/* Drawer panel */}
      <div
        className={`fixed top-0 right-0 h-full z-50 bg-zinc-900 border-l border-zinc-800 flex flex-col shadow-2xl transition-transform duration-300 ease-in-out ${width} ${open ? 'translate-x-0' : 'translate-x-full'}`}
      >
        {/* Header */}
        <div className="flex items-center gap-3 px-5 py-4 border-b border-zinc-800 flex-shrink-0">
          {icon && (
            <div className={`w-9 h-9 rounded-xl ${iconBg} flex items-center justify-center flex-shrink-0`}>
              <i className={`${icon} text-white text-base`} />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <h2 className="text-white font-bold text-sm leading-none truncate">{title}</h2>
            {subtitle && <p className="text-zinc-500 text-xs mt-1 truncate">{subtitle}</p>}
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-xl hover:bg-zinc-800 transition-all cursor-pointer flex-shrink-0"
          >
            <i className="ri-close-line text-zinc-500 text-lg" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {children}
        </div>
      </div>
    </>
  );
}
